import React, { useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNavigate } from 'react-router-dom';
import { useAccountLinking } from './auth/useAccountLinking';
import { useJWTAuth } from './auth/JWTAuthContext';
import { useEntitlements } from './EntitlementsContext';

const WalletButton = ({ className = '' }) => {
  const { publicKey, connected, disconnect } = useWallet();
  const { logout: logoutAccountLinking } = useAccountLinking();
  const { logout: logoutJWT, isAuthenticated, user } = useJWTAuth();
  const { trackLogout } = useEntitlements();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);

  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // If user has JWT authentication, show username and logout button
  if (isAuthenticated() && user) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium ${className}`}
          style={{
            backgroundColor: '#512DA8',
            fontSize: '0.875rem',
            border: 'none',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4527A0'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#512DA8'}
        >
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#4ADE80'
            }}
          />
          {user.username || `User ${user.id}`}
        </button>

        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />
            <div
              className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-20 overflow-hidden"
              style={{ border: '1px solid #E5E7EB' }}
            >
              <div className="p-3 border-b border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Logged in as</div>
                <div className="text-sm font-medium text-gray-800">
                  {user.username || `User ${user.id}`}
                </div>
                {user.wallet_address && (
                  <div className="text-xs text-gray-500 mt-1 font-mono">
                    {shortenAddress(user.wallet_address)}
                  </div>
                )}
              </div>
              <button
                onClick={async () => {
                  console.log('🔌 [WalletButton] Logging out JWT user');
                  try {
                    console.log('🔌 [WalletButton] Tracking logout to prevent auto-login...');
                    trackLogout(); // Track logout time to prevent auto-login
                    
                    console.log('🔌 [WalletButton] Clearing account linking data...');
                    logoutAccountLinking(); // Clear user data from useAccountLinking
                    
                    console.log('🔌 [WalletButton] Clearing JWT token and user data...');
                    logoutJWT(); // Clear JWT token and user data
                    
                    // Disconnect Solana wallet if connected
                    if (connected) {
                      console.log('🔌 [WalletButton] Disconnecting Solana wallet...');
                      await disconnect();
                    }
                    
                    console.log('✅ [WalletButton] Logout complete');
                    setShowDropdown(false);
                    
                    // Navigate to home page after logout
                    navigate('/');
                  } catch (error) {
                    console.error('❌ [WalletButton] Error during logout:', error);
                  }
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                Logout
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // If no JWT authentication, show wallet connection flow
  if (!connected) {
    return (
      <button
        onClick={() => navigate('/?auth=login')}
        className={`${className}`}
        style={{
          backgroundColor: '#512DA8',
          borderRadius: '0.375rem',
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          border: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          color: 'white'
        }}
      >
        Sign Up / Login
      </button>
    );
  }

  // If Solana wallet is connected but no JWT (legacy flow)
  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium ${className}`}
        style={{
          backgroundColor: '#512DA8',
          fontSize: '0.875rem',
          border: 'none',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#4527A0'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#512DA8'}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#4ADE80'
          }}
        />
        {publicKey && shortenAddress(publicKey.toString())}
      </button>

      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />
          <div
            className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-20 overflow-hidden"
            style={{ border: '1px solid #E5E7EB' }}
          >
            <div className="p-3 border-b border-gray-200">
              <div className="text-xs text-gray-500 mb-1">Connected Wallet</div>
              <div className="text-sm font-mono text-gray-800 break-all">
                {publicKey && publicKey.toString()}
              </div>
            </div>
            <button
              onClick={async () => {
                console.log('🔌 [WalletButton] Disconnecting wallet and logging out completely');
                try {
                  console.log('🔌 [WalletButton] Tracking logout to prevent auto-login...');
                  trackLogout(); // Track logout time to prevent auto-login
                  
                  console.log('🔌 [WalletButton] Clearing account linking data...');
                  logoutAccountLinking(); // Clear user data from useAccountLinking
                  
                  console.log('🔌 [WalletButton] Clearing JWT token and user data...');
                  logoutJWT(); // Clear JWT token and user data
                  
                  console.log('🔌 [WalletButton] Disconnecting Solana wallet...');
                  await disconnect(); // Disconnect Solana wallet
                  
                  console.log('✅ [WalletButton] Disconnect complete');
                  setShowDropdown(false);
                  
                  // Navigate to home page after logout
                  navigate('/');
                } catch (error) {
                  console.error('❌ [WalletButton] Error during disconnect:', error);
                }
              }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
              style={{ border: 'none', cursor: 'pointer' }}
            >
              Disconnect
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WalletButton;

